import type {
  OrderDeliveryEvidenceResponseType,
  OrderFulfillmentType,
} from "@sellar-io/api-v2/types";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  Icon,
} from "@sellar-io/components";
import dayjs from "dayjs";
import { useState } from "react";
import { Alert } from "../../alert/Alert";
import { SpinnerLegacy } from "../../spinner";
import { useOrderDeliveryEvidence } from "../../../hooks/api/v2/order/useOrderDeliveryEvidence";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";

type DeliveryEvidenceItem = OrderDeliveryEvidenceResponseType[number];

export function OrderDeliveryEvidenceSummary({
  orderId,
  fulfillmentType,
}: {
  orderId: number;
  fulfillmentType: OrderFulfillmentType;
}) {
  const { business } = useSellarCurrentBusiness();
  const featureFlags = useRemoteFeatureFlags();
  const enabled =
    !!business?.Retailer &&
    fulfillmentType === "sellarDistribution" &&
    featureFlags.isActive("deliveryEvidenceStorage");
  const evidenceQuery = useOrderDeliveryEvidence(orderId, { enabled });
  const [failedEvidenceUrls, setFailedEvidenceUrls] = useState<string[]>([]);

  if (!enabled) return null;

  const refreshEvidence = () => {
    setFailedEvidenceUrls([]);
    void evidenceQuery.refetch();
  };

  const openFullResolution = async (evidenceId: number) => {
    const evidenceWindow = window.open("about:blank", "_blank");
    if (!evidenceWindow) return;
    evidenceWindow.opener = null;

    const refreshed = await evidenceQuery.refetch();
    const evidence = refreshed.data?.find(({ id }) => id === evidenceId);

    if (!refreshed.error && evidence) {
      evidenceWindow.location.href = evidence.url;
    } else {
      evidenceWindow.close();
    }
  };

  return (
    <Card className="bg-white">
      <CardHeader className="bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <H5>Delivery evidence</H5>
          {evidenceQuery.isError && (
            <Button onClick={refreshEvidence}>Try again</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {evidenceQuery.isPending ? (
          <SpinnerLegacy />
        ) : evidenceQuery.isError ? (
          <Alert
            type="error"
            message="We couldn't load the delivery evidence for this order."
          />
        ) : evidenceQuery.data.length === 0 ? (
          <H6>No delivery evidence is available for this order yet.</H6>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {evidenceQuery.data.map((evidence) => (
              <DeliveryEvidencePreview
                key={evidence.id}
                evidence={evidence}
                failed={failedEvidenceUrls.includes(evidence.url)}
                refreshing={evidenceQuery.isFetching}
                onImageError={() =>
                  setFailedEvidenceUrls((current) =>
                    current.includes(evidence.url)
                      ? current
                      : [...current, evidence.url],
                  )
                }
                onRefresh={refreshEvidence}
                onOpen={() => void openFullResolution(evidence.id)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DeliveryEvidencePreview({
  evidence,
  failed,
  refreshing,
  onImageError,
  onRefresh,
  onOpen,
}: {
  evidence: DeliveryEvidenceItem;
  failed: boolean;
  refreshing: boolean;
  onImageError: () => void;
  onRefresh: () => void;
  onOpen: () => void;
}) {
  const label =
    evidence.evidenceType === "signature"
      ? "Recipient signature"
      : `Proof of delivery${evidence.sequence > 1 ? ` ${evidence.sequence}` : ""}`;
  const capturedAt = evidence.capturedAt ?? evidence.uploadedAt;

  return (
    <article className="flex flex-col gap-3 rounded-lg border border-zinc-200 p-3">
      {failed ? (
        <div className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-md bg-zinc-50 p-4 text-center">
          <H6>This preview has expired or could not be loaded.</H6>
          <Button disabled={refreshing} onClick={onRefresh}>
            Refresh image
          </Button>
        </div>
      ) : (
        <img
          src={evidence.url}
          alt={`${label}, captured ${dayjs(capturedAt).format("DD/MM/YYYY HH:mm")}`}
          className="h-64 w-full rounded-md bg-zinc-50 object-contain"
          onError={onImageError}
        />
      )}
      <div className="flex flex-col gap-1">
        <H5>{label}</H5>
        <H6>Captured {dayjs(capturedAt).format("DD/MM/YYYY HH:mm")}</H6>
      </div>
      <Button disabled={refreshing} onClick={onOpen}>
        <Icon size={0.7} icon="OPEN_IN_NEW" />
        Open full resolution
      </Button>
    </article>
  );
}
